import { useCallback, useEffect, useRef, useState } from "react";
import {
  Autocomplete,
  AutocompleteItem,
  Button,
  Chip,
  cn,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@heroui/react";
import { ReactComponent as X } from "../assets/img/x.svg";
import { useHomeTeam } from "../hooks/useHomeTeam";
import { useTheme } from "../hooks/useTheme";

type AutocompleteMultipleProps = {
  items: { id: string; name: string }[];
  selectedKeys: string[];
  onSelectionChange: (keys: string[]) => void;
  primaryKey?: string;
  chipColors?: Record<string, string>;
  [key: string]: any;
};

export function AutocompleteMultiple(props: AutocompleteMultipleProps) {
  const {
    items,
    selectedKeys,
    onSelectionChange,
    primaryKey = "id",
    chipColors,
    ...restProps
  } = props;
  const [inputValue, setInputValue] = useState("");
  const inputRef = useRef(null);

  function handleSelectItem(id) {
    if (!id) return;
    if (!selectedKeys.map((key) => key).includes(id)) {
      onSelectionChange([...selectedKeys, id]);
    } else handleRemoveItem(id);
    setInputValue("");
  }

  function filterToOnly(id) {
    onSelectionChange([id]);
    setInputValue("");
  }

  const filteredItems = inputValue
    ? items.filter((item) =>
        item.name.toLowerCase().includes(inputValue.toLowerCase())
      )
    : items;
  const handleRemoveItem = (id) =>
    onSelectionChange(selectedKeys.filter((i) => i !== id));

  const selectedKeysRef = useRef(selectedKeys);

  const changeFilledWithin = useCallback((filledWithin) => {
    const isFilledWithin = !!(
      filledWithin === "true" ||
      inputRef?.current?.getAttribute("data-filled-within") === "true"
    );
    const filled = isFilledWithin || selectedKeysRef.current.length;
    inputRef?.current?.parentElement?.parentElement?.parentElement?.setAttribute(
      "data-filled-within",
      !!filled ? "true" : "false"
    );
  }, []);

  useEffect(() => {
    selectedKeysRef.current = selectedKeys;
    changeFilledWithin(inputRef?.current?.getAttribute("data-filled-within"));
  }, [selectedKeys, changeFilledWithin]);

  useEffect(() => {
    const handleMutation = (mutationsList) => {
      for (let mutation of mutationsList)
        if (
          mutation.type === "attributes" &&
          mutation.attributeName === "data-filled-within"
        )
          changeFilledWithin(
            mutation?.target?.getAttribute("data-filled-within")
          );
    };

    if (inputRef.current) {
      const observer = new MutationObserver(handleMutation);
      observer.observe(inputRef.current, { attributes: true });
      return () => observer.disconnect();
    }
  }, [changeFilledWithin]);

  return (
    <Autocomplete
      ref={inputRef}
      classNames={{
        base: "overflow-hidden",
        endContentWrapper: "absolute top-[0.4px] right-3",
      }}
      startContent={selectedKeys.map((id) => {
        const player = items.find((item) => item[primaryKey] === id);
        return (
          player && (
            <PopoverLabel
              key={id}
              id={id}
              player={player}
              chipColors={chipColors}
              handleRemoveItem={handleRemoveItem}
              filterToOnly={filterToOnly}
            />
          )
        );
      })}
      endContent={
        <Button
          variant="light"
          isIconOnly
          className={cn(
            "h-10 w-6 rounded-full p-3 data-[hover=true]:bg-default/40 group-data-[hover=true]:opacity-100",
            {
              hidden: !selectedKeys.length,
            }
          )}
          onPress={() => onSelectionChange([])}
        >
          {
            <X
              className={`h-4 w-4 text-primary dark:text-white`}
              style={{ strokeWidth: 6 }}
            />
          }
        </Button>
      }
      selectedKey={null}
      isClearable={false}
      onSelectionChange={(id) => handleSelectItem(id)}
      inputValue={inputValue}
      onInputChange={setInputValue}
      inputProps={{
        classNames: {
          label:
            "mt-2.5 group-data-[filled-within=true]:translate-y-0 group-data-[filled-within=true]:mt-0",
          inputWrapper: cn("block", "bg-gray-200 dark:bg-gray-800", {
            "min-h-8": selectedKeys.length === 0,
            "h-auto": selectedKeys.length > 0,
          }),
          innerWrapper: cn(
            "flex flex-wrap justify-start gap-1 h-auto max-w-[calc(100%-4rem)]",
            {
              "mt-3 -ml-1.5": selectedKeys.length === 0,
              "mt-6": selectedKeys.length > 0,
            }
          ),
          input: "w-20 h-7",
        },
      }}
      {...restProps}
    >
      {filteredItems.map((item) => (
        <AutocompleteItem
          key={item[primaryKey]}
          textValue={item.name}
          endContent={
            selectedKeys.map((key) => key).includes(item[primaryKey]) && (
              <p>✓</p>
            )
          }
        >
          {item.name}
        </AutocompleteItem>
      ))}
    </Autocomplete>
  );
}

function PopoverLabel({
  id,
  player,
  chipColors,
  handleRemoveItem,
  filterToOnly,
}) {
  const [popoverOpen, setPopoverOpen] = useState(false);

  return (
    <Popover
      classNames={{
        content: "p-4",
      }}
      isOpen={popoverOpen}
      onOpenChange={setPopoverOpen}
    >
      <PopoverTrigger>
        <Chip
          key={id}
          classNames={{
            base: "bg-gray-300/60 dark:bg-gray-700 rounded-lg min-w-0",
            content: "truncate",
          }}
          startContent={
            chipColors?.[id] && (
              <div
                className="mx-1 h-2 w-2 rounded-full"
                style={{ backgroundColor: chipColors?.[id] }}
              />
            )
          }
        >
          {player.name}
        </Chip>
      </PopoverTrigger>
      <PopoverContent className="flex flex-row gap-4">
        <Button
          color="danger"
          onPress={() => {
            setPopoverOpen(false);
            handleRemoveItem(id);
          }}
        >
          Remove
        </Button>
        <Button
          color="primary"
          onPress={() => {
            setPopoverOpen(false);
            filterToOnly(id);
          }}
        >
          Only {player.name}
        </Button>
      </PopoverContent>
    </Popover>
  );
}
